import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Package, User, Megaphone, MessageCircle, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";

const ICONS = {
  order: Package,
  account: User,
  broadcast: Megaphone,
  chat: MessageCircle,
};
const COLORS = {
  order: "text-[#06B6D4]",
  account: "text-emerald-500",
  broadcast: "text-amber-500",
  chat: "text-violet-500",
};

const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=30");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // poll every 20s for live unread counts
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.notification_id}/read`);
        setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
        setUnread((c) => Math.max(0, c - 1));
      } catch { /* ignore */ }
    }
    // Route based on kind
    setOpen(false);
    if (n.kind === "order" && n.order_id) navigate(`/track?id=${n.order_id}`);
    else if (n.kind === "chat") navigate("/dashboard"); // chat dock is on dashboard / global
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
      toast.success("All caught up");
    } catch { toast.error("Failed"); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative p-2 rounded-full bg-white/8 hover:bg-white/14 transition-colors text-white"
          aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notification-unread-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#06B6D4] text-[10px] font-bold text-white flex items-center justify-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0 max-h-[480px] overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
          <h3 className="font-display text-base font-bold text-[#0B1E3F]">Notifications</h3>
          {unread > 0 && (
            <button onClick={markAllRead}
              className="text-xs text-[#06B6D4] font-semibold hover:underline inline-flex items-center gap-1"
              data-testid="mark-all-read-btn">
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="overflow-y-auto max-h-[400px]">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500" data-testid="notifications-empty">
              No notifications yet.
            </div>
          ) : items.map((n) => {
            const Icon = ICONS[n.kind] || Bell;
            return (
              <button key={n.notification_id} onClick={() => markRead(n)}
                data-testid={`notification-item-${n.notification_id}`}
                className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-[#F4F6FB] hover:bg-[#F8FAFC] transition-colors ${n.read ? "opacity-70" : ""}`}>
                <span className={`mt-1 ${COLORS[n.kind] || "text-neutral-500"}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#0B1E3F] truncate">{n.title}</p>
                  <p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.body}</p>
                  <p className="text-[10px] text-neutral-400 mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>
                {!n.read && <span className="mt-2 h-2 w-2 rounded-full bg-[#06B6D4] shrink-0" />}
              </button>
            );
          })}
        </div>
        <div className="p-2 border-t border-[#E2E8F0] text-center">
          <Link to="/dashboard" onClick={() => setOpen(false)}
            className="text-xs text-neutral-500 hover:text-[#0B1E3F]">Open dashboard</Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
